/**
 * A stage that serves every lane at once: the SIP Radius gateway, the gstreamer
 * ingester, the S3 recording being replayed.
 *
 * Vertically centred against the lanes it spans, which is what makes the fan-out
 * read as a fan-out rather than as a fourth column of rows.
 */

import type { ReactNode } from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';

import FlowTile from './FlowTile';
import FlowStatusBadge from './FlowStatusBadge';
import {
  MONO_FONT,
  flowBlink,
  flowToken,
  toneTextColor,
  type FlowTone,
} from './tokens';

interface FlowNodeCardProps {
  title: ReactNode;
  tone: FlowTone;
  /** What the node is doing, in its own words. Shown as a status pill. */
  statusLabel?: string;
  /** Pulse the status dot while a transition is in flight. */
  pulse?: boolean;
  /** Identifiers, addresses, paths — rendered mono, one per line. */
  lines?: ReactNode[];
  /** One sentence on what this stage does. */
  note?: ReactNode;
  /** Controls belonging to the node, such as a copy button. */
  children?: ReactNode;
}

export default function FlowNodeCard({
  title,
  tone,
  statusLabel,
  pulse = false,
  lines = [],
  note,
  children,
}: FlowNodeCardProps) {
  const theme = useTheme();
  const toneColor = toneTextColor(theme, tone);

  return (
    <FlowTile
      borderToken={tone === 'idle' ? 'wash700' : 'wash600'}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 1.5,
        py: 2.5,
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
        {/* Only when there is no pill to carry the tone. The pill has its own
            dot, and two dots one above the other say the same thing twice; but
            a card with no status at all (Simulive's S3 bucket) would otherwise
            have nothing showing its tone. */}
        {!statusLabel && (
          <Box
            sx={{
              width: 8,
              height: 8,
              flexShrink: 0,
              borderRadius: '50%',
              backgroundColor: toneColor,
              animation: pulse ? `${flowBlink} 1s infinite` : 'none',
            }}
          />
        )}
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
      </Stack>

      {statusLabel && (
        <FlowStatusBadge label={statusLabel} tone={tone} pulse={pulse} />
      )}

      {lines.map((line, index) => (
        <Typography
          // Positional: these are display lines, not entities, and a caller can
          // legitimately render the same string twice.
          key={index}
          variant="caption"
          sx={{
            fontFamily: MONO_FONT,
            color: flowToken(theme, 'wash200'),
            display: 'block',
            overflowWrap: 'break-word',
          }}
        >
          {line}
        </Typography>
      ))}

      {children}

      {note && (
        <Typography
          variant="caption"
          sx={{
            color: flowToken(theme, 'wash500'),
            display: 'block',
            lineHeight: 1.3,
          }}
        >
          {note}
        </Typography>
      )}
    </FlowTile>
  );
}
